import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSpring, animated } from '@react-spring/web';
import './Dashboard.css';
import { useTheme } from './ThemeContext';
import { getTranslation } from './translations';
import { saveAuthData, apiRequest, API_ENDPOINTS } from '../../api/config';
import LegalModal from '../LegalModal';

// API基础URL - 生产环境使用环境变量
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://666pwa.top';

// 主题图标组件
const SunIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"></circle>
    <line x1="12" y1="1" x2="12" y2="3"></line>
    <line x1="12" y1="21" x2="12" y2="23"></line>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
    <line x1="1" y1="12" x2="3" y2="12"></line>
    <line x1="21" y1="12" x2="23" y2="12"></line>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
  </svg>
);

const MoonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
  </svg>
);

const DashboardAuth = ({ initialMode = 'login' }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode); // 'login' 或 'register' 或 'forgotPassword'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState(''); // 添加验证码状态
  const [codeSending, setCodeSending] = useState(false); // 发送验证码状态
  const [codeSent, setCodeSent] = useState(false); // 验证码是否已发送
  const [countdown, setCountdown] = useState(0); // 倒计时
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // 忘记密码相关状态
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: 输入邮箱, 2: 输入验证码, 3: 设置新密码
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // 添加法律条款模态框状态
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalModalTab, setLegalModalTab] = useState('terms');

  // 使用主题上下文
  const { darkMode, toggleTheme, language, toggleLanguage } = useTheme();
  
  // 获取翻译函数
  const t = getTranslation(language);
  
  // 初始化模式
  useEffect(() => {
    if (initialMode && initialMode !== mode) {
      setMode(initialMode);
      resetForm();
    }
  }, [initialMode]);

  // 处理验证码倒计时
  useEffect(() => {
    let timer = null;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0 && codeSent) {
      setCodeSent(false);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown, codeSent]);

  // 内容切换动画
  const contentAnimation = useSpring({
    opacity: 1,
    transform: 'translateY(0)',
    from: { opacity: 0, transform: 'translateY(20px)' },
    key: mode,
    // 添加immediate条件防止不必要的动画
    immediate: isLoading,
    config: { tension: 280, friction: 20, clamp: true }
  });

  // Tab按钮动画
  const tabIndicatorAnimation = useSpring({
    left: mode === 'login' ? '0%' : '50%',
    config: { tension: 300, friction: 30 }
  });

  // 重置表单
  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setVerificationCode('');
    setCodeSent(false);
    setCountdown(0);
    setAgreeTerms(false);
    setErrors({});
    setSuccessMessage('');
    // 重置忘记密码相关状态
    setForgotPasswordStep(1);
    setNewPassword('');
    setConfirmNewPassword('');
  };

  // 切换登录/注册模式
  const toggleMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    resetForm();
  };

  // 验证邮箱格式
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // 验证密码强度
  const validatePassword = (password) => {
    // 至少8位，包含大小写字母和数字
    const re = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;
    return re.test(password);
  };

  // 发送验证码
  const sendVerificationCode = async () => {
    if (!email) {
      setErrors({ email: t('requiredEmail') });
      return;
    }

    if (!validateEmail(email)) {
      setErrors({ email: t('invalidEmail') });
      return;
    }

    setCodeSending(true);
    setErrors({});

    try {
      // 根据模式选择不同的验证码接口
      // 注册模式: /api/user/send-register-code (不检查邮箱是否已注册)
      // 忘记密码模式: /api/email/send-reset-code (检查邮箱必须已注册)
      const endpoint = mode === 'forgotPassword' 
        ? '/api/email/send-reset-code' 
        : '/api/user/send-register-code';
      
      const response = await apiRequest(
        endpoint,
        {
          email: email,
          lang: language === 'zh' ? 'zh_CN' : 'en_US'
        },
        false,
        'POST'
      );

      // 成功发送验证码
      if (response.status === 'success') {
        setCodeSent(true);
        setCountdown(60); // 60秒倒计时
        setSuccessMessage(t('verificationSent', { email }));
        
        // 自动清除成功消息
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        // 处理错误响应
        handleVerificationError(response.message);
      }
    } catch (error) {
      console.error('发送验证码错误:', error);
      
      // 详细记录错误信息，方便调试
      if (error.data) {
        console.log('错误数据:', error.data);
        console.log('错误状态码:', error.status);
      }
      
      // 处理错误响应，从error对象中提取错误信息
      if (error.data) {
        handleVerificationError(error.data.message);
      } else {
        setErrors({ verificationCode: t('networkError') });
      }
    } finally {
      setCodeSending(false);
    }
  };

  // 处理验证码错误
  const handleVerificationError = (errorMessage) => {
    console.log('处理验证码错误:', errorMessage);
    
    // 检查错误消息是否包含"邮箱已注册"
    if (errorMessage && errorMessage.includes('已注册')) {
      setErrors({ email: t('emailAlreadyRegistered') });
    } else {
      setErrors({ verificationCode: errorMessage || t('sendCodeFailed') });
    }
  };

  // 表单验证
  const validateForm = () => {
    // 创建新的错误对象而不是直接修改状态，避免多次重渲染
    const newErrors = {};

    if (!email) {
      newErrors.email = t('requiredEmail');
    } else if (mode !== 'login' && !validateEmail(email)) {
      // 登录时不验证邮箱格式，注册时验证
      newErrors.email = t('invalidEmail');
    }

    if (mode === 'login') {
      if (!password) {
        newErrors.password = t('requiredPassword');
      }
    } else {
      // 注册模式
      if (!password) {
        newErrors.password = t('requiredPassword');
      } else if (!validatePassword(password)) {
        newErrors.password = t('invalidPassword');
      }

      if (!verificationCode) {
        newErrors.verificationCode = t('requiredCode');
      } else if (verificationCode.length !== 6) {
        newErrors.verificationCode = t('invalidCode');
      }

      if (!agreeTerms) {
        newErrors.agreeTerms = t('agreeTermsError');
      }
    }

    // 仅当有错误时才更新错误状态，减少不必要的重渲染
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    
    // 如果没有错误，清空错误状态
    if (Object.keys(errors).length > 0) {
      setErrors({});
    }
    
    return true;
  };

  // 调用真实API接口
  const callApi = async (endpoint, data) => {
    try {
      // 使用apiRequest函数发送请求 - 使用正确的参数格式
      const result = await apiRequest(
        endpoint,
        data,
        false,
        'POST'
      );
      
      // 当接口返回错误状态时，直接返回错误信息
      if (result.status === 'error') {
        return { 
          success: false, 
          message: result.message || '操作失败'
        };
      }
      
      // 添加调试日志
      console.log('[callApi] 原始result:', JSON.stringify(result, null, 2));
      console.log('[callApi] result.status:', result.status);
      console.log('[callApi] result.data:', result.data);
      console.log('[callApi] result.data?.token:', result.data?.token);
      console.log('[callApi] result.token:', result.token);
      
      // 提取token
      const extractedToken = result.data?.token || result.token;
      console.log('[callApi] 提取的token:', extractedToken ? extractedToken.substring(0, 30) + '...' : 'undefined');
      
      return { 
        success: true, 
        message: result.message || '操作成功',
        data: result.data,
        // Java后端: token 在 result.data.token
        // Python后端: token 在 result.token (顶层)
        token: extractedToken
      };
    } catch (error) {
      console.error(`API调用错误 (${endpoint}):`, error);
      return { success: false, message: error.message || '网络错误，请稍后再试' };
    }
  };

  // 处理表单提交
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 防止表单验证期间的动画抖动
    const inputElements = document.querySelectorAll('input');
    inputElements.forEach(input => input.blur());
    
    if (!validateForm()) return;

    setIsLoading(true);
    setSuccessMessage('');
    setErrors({}); // 清空之前的错误信息

    try {
      // 构建请求数据
      const requestData = { email, password };
      if (mode === 'register') {
        requestData.code = verificationCode;  // 修改：使用code作为参数名
        // 其他注册所需数据
      }

      // 调用对应的API接口
      const endpoint = mode === 'login' ? API_ENDPOINTS.USER.LOGIN : API_ENDPOINTS.USER.REGISTER;
      const response = await callApi(endpoint, requestData);
      
      console.log(`[Auth] ${mode} 响应:`, JSON.stringify(response, null, 2));
      
      if (response.success) {
        setSuccessMessage(response.message);
        
        // 如果是登录成功，处理用户信息存储
        if ((mode === 'login' || mode === 'register') && response.data) {
          // 兼容Java后端和Python后端的响应格式
          let userData, token;
          
          // Java后端格式: { data: { user: {...}, token: "..." } }
          if (response.data.user && response.data.token) {
            userData = response.data.user;
            token = response.data.token;
            console.log("[Auth] 检测到 Java 后端响应格式 (user字段)");
          } 
          // Python后端格式: { data: {...}, token: "..." }
          else if (response.token) {
            userData = response.data;
            token = response.token;
            console.log("[Auth] 检测到 Python 后端响应格式");
          }
          // 后备方案
          else {
            userData = response.data.user || response.data.data || response.data;
            token = response.data.token || response.token;
            console.log("[Auth] 使用后备方案提取数据");
          }
          
          console.log("[Auth] 登录/注册成功:", { 
            userData: userData ? "已获取 (role: " + (userData.role || 'N/A') + ")" : "无数据", 
            token: token ? "已获取 (长度: " + token.length + ")" : "无令牌",
            mode
          });
          
          if (!token) {
            console.error('[Auth] 服务器未返回认证令牌，完整响应:', JSON.stringify(response, null, 2));
            setErrors({ api: '登录失败：服务器未返回认证令牌' });
            setIsLoading(false);
            return;
          }
          
          // 保存用户信息和 token 到存储，始终设置 rememberMe 为 true
          saveAuthData(userData, token, true);
          
          console.log('[Auth] 认证数据已保存:', { 
            storage: 'localStorage', // 改为始终使用 localStorage
            email: userData.email
          });
          
          // 验证数据是否成功保存
          const savedToken = localStorage.getItem('authData') || sessionStorage.getItem('authData');
          console.log('[Auth] 存储验证:', savedToken ? "数据已保存" : "保存失败");
          
          console.log('[Auth] 登录成功，准备跳转到后台管理界面...');
          
          // 延迟很短的时间再跳转，确保状态更新完成
          setTimeout(() => {
            // 根据用户角色决定跳转目标
            console.log('[Auth] 正在执行导航...');
            
            // 检查用户是否为管理员或超级管理员
            if (userData.role === 'admin' || userData.role === 'superadmin') {
              console.log('[Auth] 用户是管理员，跳转到管理员仪表盘');
              navigate('/dashboard/admin/dashboard');
            } else {
              console.log('[Auth] 用户是普通用户，跳转到用户仪表盘');
              navigate('/dashboard');
            }
          }, 300);
        }
      } else {
        // 显示API返回的错误信息
        console.log('[Auth] 操作失败:', response.message);
        setErrors({ api: response.message });
      }
    } catch (error) {
      console.error('[Auth] 表单提交错误:', error);
      setErrors({ api: '操作失败，请稍后再试' });
    } finally {
      setIsLoading(false);
    }
  };

  // 忘记密码处理
  const handleForgotPassword = async () => {
    setMode('forgotPassword');
    setForgotPasswordStep(1);
    resetForm();
    setSuccessMessage('');
    setErrors({});
  };

  // 发送密码重置验证码
  const sendResetPasswordCode = async () => {
    if (!email) {
      setErrors({ email: t('requiredEmail') });
      return;
    }

    if (!validateEmail(email)) {
      setErrors({ email: t('invalidEmail') });
      return;
    }

    setIsLoading(true);
    setCodeSending(true);
    
    try {
      // 使用apiRequest函数调用发送重置密码验证码的API，添加lang参数
      const result = await apiRequest(
        '/api/email/send-reset-code',
        {
          email: email,
          lang: language === 'zh' ? 'zh_CN' : 'en_US' // 根据当前UI语言选择邮件语言
        },
        false,
        'POST'
      );

      if (result.status === 'success') {
        setCodeSent(true);
        setCountdown(60); // 60秒倒计时
        setSuccessMessage(t('resetCodeSent', { email }));
        
        // 自动清除成功消息
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
        
        // 进入下一步
        setForgotPasswordStep(2);
      } else {
        // 处理各种错误情况
        if (result.message === '该邮箱未注册') {
          setErrors({ email: t('emailNotRegistered') });
          // 不进入下一步
        } else {
          setErrors({ api: result.message || t('sendCodeFailed') });
        }
      }
    } catch (error) {
      console.error('发送重置密码验证码错误:', error);
      
      // 详细记录错误信息，方便调试
      if (error.data) {
        console.log('错误数据:', error.data);
        console.log('错误状态码:', error.status);
      }
      
      // 处理HTTP错误状态码的情况
      if (error.data) {
        const errorData = error.data;
        if (errorData.message === '该邮箱未注册') {
          setErrors({ email: t('emailNotRegistered') });
        } else {
          setErrors({ api: errorData.message || t('sendCodeFailed') });
        }
      } else {
        setErrors({ api: t('networkError') });
      }
    } finally {
      setIsLoading(false);
      setCodeSending(false);
    }
  };

  // 验证重置密码的验证码
  const verifyResetCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setErrors({ verificationCode: t('invalidCode') });
      return;
    }

    setIsLoading(true);
    
    try {
      // 调用验证重置密码验证码的API，添加lang参数
      const result = await apiRequest(
        '/api/email/verify-reset-code',
        {
          email: email,
          code: verificationCode,
          lang: language === 'zh' ? 'zh_CN' : 'en_US' // 根据当前UI语言选择邮件语言
        },
        false,
        'POST'
      );

      if (result.status === 'success') {
        setSuccessMessage(t('codeVerified'));
        
        // 进入下一步：设置新密码
        setForgotPasswordStep(3);
        
        // 自动清除成功消息
        setTimeout(() => {
          setSuccessMessage('');
        }, 2000);
      } else {
        setErrors({ verificationCode: result.message || t('invalidOrExpiredCode') });
      }
    } catch (error) {
      console.error('验证重置密码验证码错误:', error);
      
      // 详细记录错误信息，方便调试
      if (error.data) {
        console.log('错误数据:', error.data);
        console.log('错误状态码:', error.status);
        setErrors({ verificationCode: error.data.message || t('invalidOrExpiredCode') });
      } else {
        setErrors({ api: t('verificationFailed') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 提交新密码
  const submitNewPassword = async () => {
    // 密码验证
    if (!newPassword) {
      setErrors({ newPassword: t('requiredPassword') });
      return;
    }
    
    if (!validatePassword(newPassword)) {
      setErrors({ newPassword: t('invalidPassword') });
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      setErrors({ confirmNewPassword: t('passwordMismatch') });
      return;
    }

    setIsLoading(true);
    
    try {
      // 调用重置密码的API，添加lang参数
      const result = await apiRequest(
        '/api/user/reset-password',
        {
          email: email,
          verificationCode: verificationCode,
          newPassword: newPassword
        },
        false,
        'POST'
      );

      if (result.status === 'success') {
        setSuccessMessage(t('passwordResetSuccess'));
        
        // 3秒后自动切换到登录模式
        setTimeout(() => {
          setMode('login');
          resetForm();
        }, 3000);
      } else {
        setErrors({ api: result.message || t('passwordResetFailed') });
      }
    } catch (error) {
      console.error('重置密码错误:', error);
      
      // 详细记录错误信息，方便调试
      if (error.data) {
        console.log('错误数据:', error.data);
        console.log('错误状态码:', error.status);
        setErrors({ api: error.data.message || t('passwordResetFailed') });
      } else {
        setErrors({ api: t('passwordResetFailed') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 处理忘记密码表单提交
  const handleForgotPasswordSubmit = (e) => {
    e.preventDefault();
    
    // 根据当前步骤执行不同操作
    switch (forgotPasswordStep) {
      case 1:
        // 发送验证码 - 由sendResetPasswordCode内部决定是否进入下一步
        sendResetPasswordCode();
        break;
      case 2:
        // 验证验证码
        verifyResetCode();
        break;
      case 3:
        // 提交新密码
        submitNewPassword();
        break;
      default:
        break;
    }
  };

  // 处理表单字段变化，避免频繁重渲染
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // 清除相关字段的错误提示
    if (errors[name]) {
      const updatedErrors = {...errors};
      delete updatedErrors[name];
      setErrors(updatedErrors);
    }
    
    // 对于checkbox类型的输入，使用checked属性
    if (type === 'checkbox') {
      if (name === 'agreeTerms') {
        setAgreeTerms(checked);
      }
    } 
    // 对于验证码输入，移除非数字字符
    else if (name === 'verificationCode') {
      const numericValue = value.replace(/\D/g, '');
      setVerificationCode(numericValue);
    }
    // 对于其他类型的输入，直接更新对应状态
    else if (name === 'email') {
      setEmail(value);
    } else if (name === 'password') {
      setPassword(value);
    } else if (name === 'confirmPassword') {
      setConfirmPassword(value);
    } else if (name === 'newPassword') {
      setNewPassword(value);
    } else if (name === 'confirmNewPassword') {
      setConfirmNewPassword(value);
    }
  };

  // 打开法律条款模态框
  const openLegalModal = (tab) => {
    setLegalModalTab(tab);
    setLegalModalOpen(true);
  };
  
  // 关闭法律条款模态框
  const closeLegalModal = () => {
    setLegalModalOpen(false);
  };

  return (
    <div className="auth-container">
      {/* 主要内容 */}
      <div className="auth-content">
        {/* 左侧装饰区域 */}
        <div className="auth-decoration">
          <div className="auth-decoration-content">
            <div className="auth-logo">
              <span className="logo-mark" style={{ width: '40px', height: '40px', fontSize: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}></span>
              <span>{language === 'en' ? 'MagicAudit' : '奇迹妙审'}</span>
            </div>
            <div className="auth-decoration-text">
              <h1>{t('welcomeBack')}</h1>
              <p>{mode === 'login' ? t('loginToContinue') : mode === 'register' ? t('registerToContinue') : t('resetPassword')}</p>
            </div>
          </div>
          <div className="auth-decoration-footer">
            <p>{t('tagline')}</p>
          </div>
        </div>

        {/* 右侧表单区域 */}
        <div className="auth-form-container">
          {/* 语言和主题切换 */}
          <div className="auth-settings">
            <button
              className="auth-theme-toggle"
              onClick={toggleTheme}
              aria-label={darkMode ? t('lightMode') : t('darkMode')}
              title={darkMode ? t('lightMode') : t('darkMode')}
            >
              {darkMode ? <SunIcon /> : <MoonIcon />}
            </button>
            
            <button
              className="auth-language-toggle"
              onClick={toggleLanguage}
              aria-label={language === 'zh' ? t('english') : t('chinese')}
              title={language === 'zh' ? t('english') : t('chinese')}
            >
              {language === 'zh' ? 'EN' : '中'}
            </button>
          </div>

          {/* 返回首页 */}
          <div className="auth-back-home">
            <button className="auth-back-link" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }} onClick={() => { window.location.href = window.location.origin + '/'; }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              {t('backToHome')}
            </button>
          </div>

          <div className="auth-form-content">
            <div className="auth-form-header">
              <h2>{mode === 'login' ? t('login') : mode === 'register' ? t('register') : t('resetPassword')}</h2>
              {mode !== 'forgotPassword' && (
                <p>
                  {mode === 'login' ? t('noAccount') : t('hasAccount')}
                  <button onClick={toggleMode} className="auth-toggle-mode-btn">
                    {mode === 'login' ? t('switchToRegister') : t('switchToLogin')}
                  </button>
                </p>
              )}
              {mode === 'forgotPassword' && (
                <p>
                  <button onClick={() => setMode('login')} className="auth-toggle-mode-btn">
                    {t('backToLogin')}
                  </button>
                </p>
              )}
            </div>

            {/* 标签切换栏 - 忘记密码模式下不显示 */}
            {mode !== 'forgotPassword' && (
              <div className="auth-tabs">
                <div className="auth-tabs-buttons">
                  <button
                    onClick={() => setMode('login')}
                    className={`auth-tab-btn ${mode === 'login' ? 'active' : ''}`}
                  >
                    {t('login')}
                  </button>
                  <button
                    onClick={() => setMode('register')}
                    className={`auth-tab-btn ${mode === 'register' ? 'active' : ''}`}
                  >
                    {t('register')}
                  </button>
                </div>
                
                {/* 活动标签指示器 */}
                <animated.div 
                  style={tabIndicatorAnimation}
                  className="auth-tab-indicator"
                />
              </div>
            )}

            {/* 忘记密码模式下显示步骤指示器 */}
            {mode === 'forgotPassword' && (
              <div className="auth-steps-indicator">
                <div className={`auth-step ${forgotPasswordStep >= 1 ? 'active' : ''}`}>
                  <div className="auth-step-circle">{1}</div>
                  <div className="auth-step-label">{t('emailVerification')}</div>
                </div>
                <div className={`auth-step-line ${forgotPasswordStep > 1 ? 'active' : ''}`}></div>
                <div className={`auth-step ${forgotPasswordStep >= 2 ? 'active' : ''}`}>
                  <div className="auth-step-circle">{2}</div>
                  <div className="auth-step-label">{t('verificationCode')}</div>
                </div>
                <div className={`auth-step-line ${forgotPasswordStep > 2 ? 'active' : ''}`}></div>
                <div className={`auth-step ${forgotPasswordStep >= 3 ? 'active' : ''}`}>
                  <div className="auth-step-circle">{3}</div>
                  <div className="auth-step-label">{t('resetPassword')}</div>
                </div>
              </div>
            )}
            
            {/* 错误消息 - 强调API错误的显示 */}
            {errors.api && (
              <div style={{ backgroundColor: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>
                <span style={{ marginRight: '6px' }}>⚠</span>{errors.api}
              </div>
            )}
            
            {/* 成功消息 */}
            {successMessage && (
              <div className="auth-success-message">
                {successMessage}
              </div>
            )}

            {/* 表单内容 - 使用动画切换 */}
            <animated.div style={contentAnimation}>
              {mode === 'forgotPassword' ? (
                <form onSubmit={handleForgotPasswordSubmit} className="auth-form">
                  {/* 第1步：输入邮箱 */}
                  {forgotPasswordStep === 1 && (
                    <div className="auth-form-group">
                      <p className="auth-form-description">{t('enterRegisteredEmail')}</p>
                      <label htmlFor="reset-email">{t('emailAddress')}</label>
                      <input 
                        id="reset-email"
                        name="email"
                        type="email" 
                        className={errors.email ? 'error' : ''}
                        placeholder={t('emailAddress')} 
                        value={email}
                        onChange={handleInputChange}
                        disabled={isLoading}
                      />
                      {errors.email && <div className="auth-field-error">{errors.email}</div>}
                    </div>
                  )}
                  
                  {/* 第2步：输入验证码 */}
                  {forgotPasswordStep === 2 && (
                    <div className="auth-form-group">
                      <p className="auth-form-description">{t('codeHasBeenSent', { email })}</p>
                      <label htmlFor="reset-verification-code">{t('verificationCode')}</label>
                      <div className="auth-verification-group">
                        <input 
                          id="reset-verification-code"
                          name="verificationCode"
                          type="text" 
                          className={errors.verificationCode ? 'error' : ''}
                          placeholder={t('codePlaceholder')} 
                          maxLength={6}
                          value={verificationCode}
                          onChange={handleInputChange}
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          className={`auth-send-code-btn ${codeSent ? 'disabled' : ''}`}
                          onClick={sendResetPasswordCode}
                          disabled={codeSent || codeSending || isLoading}
                        >
                          {codeSending ? (
                            <div className="auth-loading-indicator">
                              <svg className="auth-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              {t('sending')}
                            </div>
                          ) : countdown > 0 ? t('resendCodeIn', { seconds: countdown }) : t('resendCode') }
                        </button>
                      </div>
                      {errors.verificationCode && <div className="auth-field-error">{errors.verificationCode}</div>}
                      <p className="auth-hint">{t('codeValidTime')}</p>
                    </div>
                  )}
                  
                  {/* 第3步：设置新密码 */}
                  {forgotPasswordStep === 3 && (
                    <>
                      <div className="auth-form-group">
                        <label htmlFor="new-password">{t('newPassword')}</label>
                        <input 
                          id="new-password"
                          name="newPassword"
                          type="password" 
                          className={errors.newPassword ? 'error' : ''}
                          placeholder={t('passwordRule')} 
                          value={newPassword}
                          onChange={handleInputChange}
                          disabled={isLoading}
                        />
                        {errors.newPassword && <div className="auth-field-error">{errors.newPassword}</div>}
                      </div>
                      
                      <div className="auth-form-group">
                        <label htmlFor="confirm-new-password">{t('confirmNewPassword')}</label>
                        <input 
                          id="confirm-new-password"
                          name="confirmNewPassword"
                          type="password" 
                          className={errors.confirmNewPassword ? 'error' : ''}
                          placeholder={t('confirmPasswordPlaceholder')} 
                          value={confirmNewPassword}
                          onChange={handleInputChange}
                          disabled={isLoading}
                        />
                        {errors.confirmNewPassword && <div className="auth-field-error">{errors.confirmNewPassword}</div>}
                      </div>
                    </>
                  )}
                  
                  {/* 提交按钮 */}
                  <button 
                    type="submit"
                    className={`auth-submit-btn ${isLoading ? 'loading' : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="auth-loading-indicator">
                        <svg className="auth-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('processing')}
                      </div>
                    ) : (
                      forgotPasswordStep === 1 ? t('getVerificationCode') : 
                      forgotPasswordStep === 2 ? t('verify') : 
                      t('resetPassword')
                    )}
                  </button>
                </form>
              ) : (
                // 原来的登录/注册表单
                <form onSubmit={handleSubmit} className="auth-form">
                  {/* 邮箱 */}
                  <div className="auth-form-group">
                    <label htmlFor="email">
                      {t('emailAddress')}
                    </label>
                    <input 
                      id="email"
                      name="email"
                      type="email" 
                      className={errors.email ? 'error' : ''}
                      placeholder={t('emailAddress')} 
                      value={email}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                    {errors.email && <div className="auth-field-error">{errors.email}</div>}
                  </div>
                  
                  {/* 密码 */}
                  <div className="auth-form-group">
                    <label htmlFor="password">
                      {t('password')}
                    </label>
                    <input 
                      id="password"
                      name="password"
                      type="password" 
                      className={errors.password ? 'error' : ''}
                      placeholder={mode === 'login' ? t('passwordPlaceholder') : t('passwordRule')} 
                      value={password}
                      onChange={handleInputChange}
                      disabled={isLoading}
                    />
                    {errors.password && <div className="auth-field-error">{errors.password}</div>}
                  </div>
                  
                  {/* 验证码（仅注册时显示） */}
                  {mode === 'register' && (
                    <div className="auth-form-group">
                      <label htmlFor="verificationCode">
                        {t('verificationCode')}
                      </label>
                      <div className="auth-verification-group">
                        <input 
                          id="verificationCode"
                          name="verificationCode"
                          type="text" 
                          className={errors.verificationCode ? 'error' : ''}
                          placeholder={t('codePlaceholder')} 
                          maxLength={6}
                          value={verificationCode}
                          onChange={handleInputChange}
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          className={`auth-send-code-btn ${codeSent ? 'disabled' : ''}`}
                          onClick={sendVerificationCode}
                          disabled={codeSent || codeSending || isLoading}
                        >
                          {codeSending ? (
                            <div className="auth-loading-indicator">
                              <svg className="auth-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              {t('sending')}
                            </div>
                          ) : countdown > 0 ? t('resendCodeIn', { seconds: countdown }) : t('sendCode') }
                        </button>
                      </div>
                      {errors.verificationCode && <div className="auth-field-error">{errors.verificationCode}</div>}
                    </div>
                  )}
                  
                  {/* 登录时的额外选项 */}
                  {mode === 'login' && (
                    <div className="auth-options">
                      <div className="auth-forgot-password-container">
                        <button 
                          type="button"
                          className="auth-forgot-password"
                          onClick={handleForgotPassword}
                          disabled={isLoading}
                        >
                          {t('forgotPassword')}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* 注册时的同意条款选项 */}
                  {mode === 'register' && (
                    <div className="auth-terms">
                      <div className="auth-checkbox-group">
                        <input 
                          id="agreeTerms"
                          name="agreeTerms"
                          type="checkbox" 
                          className={errors.agreeTerms ? 'error' : ''}
                          checked={agreeTerms}
                          onChange={handleInputChange}
                          disabled={isLoading}
                        />
                        <label htmlFor="agreeTerms">
                          {t('agreeTerms')} 
                          <button 
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              openLegalModal('terms');
                            }}
                            className="auth-link"
                          >
                            {t('termsOfService')}
                          </button> 
                          {t('and')} 
                          <button 
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              openLegalModal('privacy');
                            }}
                            className="auth-link"
                          >
                            {t('privacyPolicy')}
                          </button>
                        </label>
                      </div>
                      {errors.agreeTerms && <div className="auth-field-error">{errors.agreeTerms}</div>}
                    </div>
                  )}
                  
                  {/* 提交按钮 */}
                  <button 
                    type="submit"
                    className={`auth-submit-btn ${isLoading ? 'loading' : ''}`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="auth-loading-indicator">
                        <svg className="auth-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('processing')}
                      </div>
                    ) : (
                      mode === 'login' ? t('login') : t('register')
                    )}
                  </button>
                </form>
              )}
            </animated.div>
          </div>
        </div>
      </div>
      
      {/* 法律条款模态框 */}
      <LegalModal 
        isOpen={legalModalOpen} 
        onClose={closeLegalModal}
        initialTab={legalModalTab}
      />
    </div>
  );
};

export default DashboardAuth;